const canUseLocalStorage = () => {
    try {
        const testKey = '__storage_test__';
        localStorage.setItem(testKey, '1');
        localStorage.removeItem(testKey);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Učitavanje iz templejta u localStorage vršiti isključivo u userData.html.twig, koji sme biti uključen isključivo kao
 * ESI u layout.html.twig na samom vrhu. Ne na drugim mestima, jer ovaj "servis" se poziva u više okruženja (iz više
 * entry pointa na istoj stranici) i to može remetiti stanje localStorage-a.
 */
export default class userData {
    static noUserValue = '0';

    static canUseLocalStorage = canUseLocalStorage();

    /**
     * Kada nije podržan localStorage niti iz HTML-a.
     */
    static fallbackToken = '0';

    static fallbackTokenHasBeenSet = false;

    /**
     * Vraća parsovan User objekt ili '0' ako korisnik nije ulogiran.
     *
     * @returns {object|'0'}
     */
    static getDecodedUserDataFromLocalStorage() {
        return this.decodeToken(this.getTokenFromLocalStorage());
    }

    /**
     * Koristi se kada šaljemo zahtev bekendu i potrebna je autorizacija korisnika.
     *
     * Ovu funkciju najčešće treba koristiti axios servis za podešavanje Adriatic-Bearer HTTP headera.
     * Kada nije ulogiran, string koji vraća je '0'.
     */
    static getTokenFromLocalStorage() {
        if (this.canUseLocalStorage) {
            return localStorage.getItem('userJWT') ?? this.noUserValue;
        }
        if (this.fallbackTokenHasBeenSet) {
            return this.fallbackToken;
        }
        return getTokenFromTemplate() ?? this.noUserValue;
    }

    /**
     * Koristi se kada radimo login bez reloada stranice ili podržavamo SSR user dependant dijelove.
     *
     * @param {string} token - Korisnički JSON Web Token.
     */
    static setTokenToLocalStorage(token) {
        if (this.canUseLocalStorage) {
            localStorage.setItem('userJWT', token);
        } else {
            this.fallbackTokenHasBeenSet = true;
            this.fallbackToken = token;
        }
    }

    /**
     * Koristi se kada radimo logout bez reloada stranice.
     */
    static removeTokenFromLocalStorage() {
        if (this.canUseLocalStorage) {
            localStorage.setItem('userJWT', this.noUserValue);
        } else {
            this.fallbackTokenHasBeenSet = true;
            this.fallbackToken = this.noUserValue;
        }
    }

    /**
     * Dekodira korisnički JSON Web Token.
     *
     * Segmenti tokena su odvojeni točkom, drugi po redu predstavlja payload (informacije o korisniku). Segmenti su
     * Base64 url safe enkodirani.
     *
     * VAŽNA NAPOMENA: ovo samo dekodira sadržaj, ne verificira vjerodostojnost tokena. Oslanjamo se na serversku
     * validaciju istoga.
     *
     * @param {string} token - Korisnički JSON Web Token (JWT)
     * @returns {object|'0'} - Parsovan User objekt ili '0' ako nije ulogiran ili nije validan
     */
    static decodeToken(token) {
        if (typeof token !== 'string') return this.noUserValue;

        const parts = token.split('.');
        if (parts.length < 2) return this.noUserValue;

        // Uspješno parsira UTF-8 karaktere i po ECMAScript standardu,
        // sam atob nije po standardu jer vraća Latin-1 (ISO-8859-1) i nije obećano da radi za unicode u Reactu.
        try {
            let base64 = parts[1]
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            while (base64.length % 4 !== 0) {
                base64 += '=';
            }
            const json = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
                    .join(''),
            );

            return JSON.parse(json);
        } catch (e) {
            return this.noUserValue;
        }
    }
}
// Module-level functije (ne eksportovane, privatne na nivou fajla)

/**
 * Dohvaća korisnički JSON Web Token proslijeđen iz backenda putem template-a stranice.
 */
function getTokenFromTemplate() {
    if (typeof document === 'undefined') {
        return null;
    }

    return document.getElementById('userData')?.getAttribute('data-user') || null;
}
